/**
 * Zentrale Spielkonstanten und Regelgrenzen.
 *
 * Alles, was Client und Server gemeinsam kennen müssen, steht hier — damit die
 * UI niemals eine Kombination anbietet, die der Server anschließend ablehnt.
 */

/** 2: Sonderrollen haben `GameSettings` und `RoomView` um Pflichtfelder erweitert. */
export const PROTOCOL_VERSION = 2;

export const MIN_PLAYERS = 3;
export const MAX_PLAYERS = 15;
export const MAX_NAME_LENGTH = 20;

export const MAFIA_COUNTS = [1, 2, 3] as const;
export type MafiaCount = (typeof MAFIA_COUNTS)[number];

/** GDD §3: 1 Mafia ab 3 Spielern, 2 ab 5, 3 ab 7. */
export const MIN_PLAYERS_FOR_MAFIA: Readonly<Record<MafiaCount, number>> = {
  1: 3,
  2: 5,
  3: 7,
};

/**
 * Die vom Host einzeln zuschaltbaren Sonderrollen.
 *
 * Jede aktivierte Rolle wird genau einmal vergeben, steht auf Bürgerseite und
 * ersetzt einen einfachen Bürger. Die Reihenfolge ist verbindlich: sie bestimmt,
 * welcher Platz der gemischten Spielerliste welche Rolle bekommt, und macht die
 * Verteilung damit bei gleichem Seed reproduzierbar.
 */
export const SPECIAL_ROLES = ['BLESSING', 'VOTE_BLOCK', 'DEAD_REDEMPTION', 'DOUBLE_UP'] as const;
export type SpecialRole = (typeof SPECIAL_ROLES)[number];

/**
 * Schlüssel der Host-Schalter in `GameSettings.specialRoles`.
 *
 * Bewusst camelCase und damit nie identisch mit den Rollen-Literalen: die
 * Geheimhaltungstests prüfen die serialisierte Sicht stumpf mit
 * `not.toContain('BLESSING')`. Hießen die Settings-Keys wie die Rollen, wäre
 * dieser schärfste aller Tests nicht mehr formulierbar, weil das Regelwerk
 * (welche Rollen im Spiel sind) öffentlich sein muss.
 */
export const SPECIAL_ROLE_SETTING_KEYS = {
  BLESSING: 'blessing',
  VOTE_BLOCK: 'voteBlock',
  DEAD_REDEMPTION: 'deadRedemption',
  DOUBLE_UP: 'doubleUp',
} as const satisfies Record<SpecialRole, string>;

export type SpecialRoleSettingKey = (typeof SPECIAL_ROLE_SETTING_KEYS)[SpecialRole];

/** So viele einfache Bürger müssen neben Mafia und Sonderrollen übrig bleiben. */
export const MIN_PLAIN_CITIZENS = 2;

/** Wie viele Spieler diese Rollenaufstellung mindestens braucht. */
export function minPlayersFor(mafiaCount: number, specialRoleCount: number): number {
  return Math.max(MIN_PLAYERS, mafiaCount + specialRoleCount + MIN_PLAIN_CITIZENS);
}

/**
 * Wie viele Sonderrollen bei dieser Spieler- und Mafia-Zahl höchstens aktiv sein
 * dürfen. Gegenstück zu `allowedMafiaCounts`: die Lobby blendet damit Schalter
 * aus, die der Server ohnehin ablehnen würde.
 */
export function maxSpecialRoles(playerCount: number, mafiaCount: number): number {
  const room = playerCount - mafiaCount - MIN_PLAIN_CITIZENS;
  return Math.max(0, Math.min(SPECIAL_ROLES.length, room));
}

/**
 * Grenzen für die vom Host konfigurierbaren Timer. Der Server klemmt jeden Wert
 * hier hinein — ohne das könnte ein manipulierter Client 99999 Sekunden setzen.
 */
export const SETTING_LIMITS = {
  startCountdownSeconds: { min: 0, max: 10, default: 3 },
  discussionSeconds: { min: 15, max: 600, default: 60 },
  votingSeconds: { min: 15, max: 300, default: 45 },
  tieBreakerSeconds: { min: 10, max: 300, default: 30 },
  maxTieRounds: { min: 1, max: 5, default: 3 },
} as const;

export type NumericSettingKey = keyof typeof SETTING_LIMITS;

/**
 * Feste Phasendauern. Bewusst nicht konfigurierbar: sie steuern Dramaturgie,
 * nicht Spielregeln, und ein Host soll sie nicht versehentlich kaputt stellen.
 */
export const ROLE_REVEAL_MAX_SECONDS = 90;
export const TIE_ANNOUNCE_SECONDS = 6;
export const ROUND_RESULT_SECONDS = 8;

/** Alle Mafia-Anzahlen, die bei dieser Spielerzahl zulässig sind. */
export function allowedMafiaCounts(playerCount: number): MafiaCount[] {
  return MAFIA_COUNTS.filter((count) => playerCount >= MIN_PLAYERS_FOR_MAFIA[count]);
}

export function isMafiaCountValid(
  playerCount: number,
  mafiaCount: number,
): mafiaCount is MafiaCount {
  return (allowedMafiaCounts(playerCount) as number[]).includes(mafiaCount);
}

/**
 * Klemmt die Mafia-Anzahl auf den höchsten zulässigen Wert.
 *
 * Wird gebraucht, wenn Spieler die Lobby verlassen: aus „6 Spieler / 3 Mafia"
 * wird beim Verlassen zweier Spieler sonst eine ungültige Einstellung.
 */
export function clampMafiaCount(playerCount: number, desired: number): MafiaCount {
  const allowed = allowedMafiaCounts(playerCount);
  if (allowed.length === 0) return 1;
  for (let i = allowed.length - 1; i >= 0; i -= 1) {
    const candidate = allowed[i]!;
    if (candidate <= desired) return candidate;
  }
  return allowed[0]!;
}

/** Klemmt einen numerischen Timer-Wert in seine erlaubten Grenzen. */
export function clampSetting(key: NumericSettingKey, value: number): number {
  const { min, max } = SETTING_LIMITS[key];
  if (!Number.isFinite(value)) return SETTING_LIMITS[key].default;
  return Math.min(max, Math.max(min, Math.round(value)));
}
